// ===========================================
// DevPad Main App Component
// ===========================================

import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProjectProvider } from './context/ProjectContext';

// Pages
import Landing from './pages/Landing';  // Public landing page
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ProjectDetail from './pages/ProjectDetail';
import Analytics from './pages/Analytics';
import DevPrompts from './pages/DevPrompts';  // AI IDE Prompt Library
import DevNotes from './pages/DevNotes';      // Developer Knowledge Notebook
import DevChat from './pages/DevChat';        // Global Dev Chat
import DevProfile from './pages/DevProfile';  // Developer Profile & Activity
import Settings from './pages/Settings';

// Components
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';

// ===========================================
// Color theme configuration - matches Settings.js
// ===========================================
const COLOR_THEMES = [
  { name: 'Amber', value: '#f59e0b', hover: '#fbbf24' },
  { name: 'Green', value: '#22c55e', hover: '#4ade80' },
  { name: 'Blue', value: '#3b82f6', hover: '#60a5fa' },
  { name: 'Purple', value: '#a855f7', hover: '#c084fc' },
  { name: 'Pink', value: '#ec4899', hover: '#f472b6' },
  { name: 'Red', value: '#ef4444', hover: '#f87171' },
  { name: 'Cyan', value: '#06b6d4', hover: '#22d3ee' },
  { name: 'Teal', value: '#14b8a6', hover: '#2dd4bf' },
  { name: 'Indigo', value: '#6366f1', hover: '#818cf8' },
  { name: 'Orange', value: '#f97316', hover: '#fb923c' },
];

// ===========================================
// Public routes where we show default amber branding
// ===========================================
const PUBLIC_ROUTES = ['/', '/welcome', '/login', '/register'];

function App() {
  const location = useLocation();
  
  // ===========================================
  // Load saved color theme on app initialization
  // Skip on public routes to show default amber branding
  // ===========================================
  useEffect(() => {
    // Check if current route is public - show default amber branding
    const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);
    
    if (isPublicRoute) {
      // Apply default amber theme for public pages (brand consistency)
      document.documentElement.style.setProperty('--color-accent', '#f59e0b');
      document.documentElement.style.setProperty('--color-accent-hover', '#fbbf24');
      document.documentElement.style.setProperty('--color-accent-muted', '#f59e0b1f');
      document.documentElement.style.setProperty('--color-border-accent', '#f59e0b4d');
    } else {
      // Apply user's saved color preference on authenticated pages
      const savedColor = localStorage.getItem('devpad_accent_color');
      if (savedColor) {
        const theme = COLOR_THEMES.find(t => t.value === savedColor) || COLOR_THEMES[0];
        document.documentElement.style.setProperty('--color-accent', theme.value);
        document.documentElement.style.setProperty('--color-accent-hover', theme.hover);
        document.documentElement.style.setProperty('--color-accent-muted', `${theme.value}1f`);
        document.documentElement.style.setProperty('--color-border-accent', `${theme.value}4d`);
      }
    }
  }, [location.pathname]);

  // ===========================================
  // Session Expiration Handler
  // ===========================================
  // Listens for session expiration events from the API service
  // and shows a user-friendly notification before redirect
  useEffect(() => {
    const handleSessionExpired = (event) => {
      // Show a simple notification before redirect
      // This can be enhanced with a toast library if available
      const message = event.detail?.reason || 'Your session has expired. Please log in again.';
      
      // Create a simple toast-style notification
      const toast = document.createElement('div');
      toast.style.cssText = `
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #ef4444;
        color: white;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 14px;
        z-index: 10000;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        animation: slideUp 0.3s ease-out;
      `;
      toast.textContent = message;
      document.body.appendChild(toast);
      
      // Remove after animation (redirect will happen via API service)
      setTimeout(() => toast.remove(), 3000);
    };

    window.addEventListener('devpad:session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('devpad:session-expired', handleSessionExpired);
    };
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ProjectProvider>
          <Routes>
            {/* Public Routes - Landing page at root for unauthenticated users */}
            <Route path="/" element={<Landing />} />
            <Route path="/welcome" element={<Landing />} />  {/* Alias for backwards compat */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            
            {/* Protected Routes - wrapped in Layout, under /dashboard */}
            <Route path="/dashboard" element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="prompts" element={<DevPrompts />} />
              <Route path="notes" element={<DevNotes />} />
              <Route path="chat" element={<DevChat />} />
              <Route path="profile" element={<DevProfile />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            
            {/* Fallback redirect to landing page */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ProjectProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;

